import { useCallback, useEffect, useMemo, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import {
  BoundaryConfig,
  BoundaryConstraints,
  ContainerDimensions,
} from 'features/content-zoom/types';
import {
  applyDragResistance,
  calculateBoundaryConstraints,
  constrainToBounds,
  defaultBoundaryConfig,
  isOutOfBounds,
} from 'features/content-zoom/utils/boundaryUtils';

interface UseBoundaryConstraintsProps {
  scale: number;
  offsetX: number;
  offsetY: number;
  config?: Partial<BoundaryConfig>;
  onPositionChange: (offsetX: number, offsetY: number) => void;
}

interface UseBoundaryConstraintsReturn {
  containerRef: (node: HTMLElement | null) => void;
  boundaries: BoundaryConstraints;
  constrainPosition: (x: number, y: number) => { offsetX: number; offsetY: number };
  applyResistance: (x: number, y: number) => { offsetX: number; offsetY: number };
  isPositionOutOfBounds: (x: number, y: number) => boolean;
  containerDimensions: ContainerDimensions | null;
}

export const useBoundaryConstraints = ({
  scale,
  offsetX,
  offsetY,
  config = {},
  onPositionChange,
}: UseBoundaryConstraintsProps): UseBoundaryConstraintsReturn => {
  const boundaryConfig = useMemo(() => ({ ...defaultBoundaryConfig, ...config }), [config]);

  const [containerDimensions, setContainerDimensions] = useState<ContainerDimensions | null>(null);

  const { ref: containerResizeRef } = useResizeDetector({
    refreshMode: 'debounce',
    refreshRate: 16,
    onResize: ({ width, height }) => {
      if (width && height) {
        setContainerDimensions({ width, height });
      }
    },
  });

  const containerRef = useCallback(
    (node: HTMLElement | null) => {
      containerResizeRef.current = node;
    },
    [containerResizeRef]
  );

  const boundaries = useMemo(() => {
    if (!containerDimensions) {
      return {
        minX: -Infinity,
        maxX: Infinity,
        minY: -Infinity,
        maxY: Infinity,
      };
    }

    return calculateBoundaryConstraints(scale, containerDimensions);
  }, [scale, containerDimensions]);

  const constrainPosition = useCallback(
    (x: number, y: number) => constrainToBounds(x, y, boundaries),
    [boundaries]
  );

  const applyResistance = useCallback(
    (x: number, y: number) => applyDragResistance(x, y, boundaries, boundaryConfig, scale),
    [boundaries, boundaryConfig, scale]
  );

  const isPositionOutOfBounds = useCallback(
    (x: number, y: number) => isOutOfBounds(x, y, boundaries),
    [boundaries]
  );

  useEffect(() => {
    if (boundaryConfig.maxDragDistance !== 0 && isPositionOutOfBounds(offsetX, offsetY)) {
      const constrainedPosition = constrainPosition(offsetX, offsetY);
      onPositionChange(constrainedPosition.offsetX, constrainedPosition.offsetY);
    }
  }, [
    scale,
    boundaryConfig.maxDragDistance,
    offsetX,
    offsetY,
    isPositionOutOfBounds,
    constrainPosition,
    onPositionChange,
  ]);

  return {
    containerRef,
    boundaries,
    constrainPosition,
    applyResistance,
    isPositionOutOfBounds,
    containerDimensions,
  };
};
